import { useQuery } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce/lib';
import { Queries } from '@shared/api/queries';
import { RelatedCardComponent } from '@shared/scenes/workflows/config';
import { useWorkflowDispatch } from '@shared/scenes/workflows/context/context';
import { API } from '@shared/types/api';

interface Card {
  name: string;
  id: string;
  isSelected: boolean;
}

export interface DefaultRelatedCardSelectionHooks {
  keyword: string;
  onSearch: (keyword: string) => void;
  selectedCards: Card[];
  cards: Card[];
  cardsHasMore?: boolean;
  reset: VoidFunction;
  loading: boolean;
  onToggleDefault: (cardId: string) => void;
}

export function useDefaultRelatedCardHooks(
  workflowTemplateId: string | null,
  selectedCardIds: string[] | undefined,
  onCardSelected: (cardId: string) => void,
) {
  const [keyword, _setKeyword] = useState<string>('');
  const [setKeyword] = useDebouncedCallback(_setKeyword, 100);
  const [selectedCards, setSelectedCards] = useState<Card[]>([]);
  const localCards = useRef(new Map<string, Card>());
  const [neededCardIds, setNeededCards] = useState<string[]>([]);

  const listSummaryCards = useQuery<API.ListSummaryCardsQuery, API.ListSummaryCardsQueryVariables>(
    Queries.card.listSummaryCards,
    {
      variables: {
        input: {
          nameQuery: keyword,
          workflowTemplateId,
        },
      },
      skip: isNil(workflowTemplateId),
      fetchPolicy: 'network-only',
      onCompleted: data => {
        data?.listSummaryCards?.cards?.forEach(cardResult => {
          if (!localCards.current.has(cardResult.id)) {
            localCards.current.set(cardResult.id, {
              id: cardResult.id,
              name: cardResult.name,
              isSelected: false,
            });
          }
        });
      },
    },
  );

  const getCards = useQuery<API.GetCardsQuery, API.GetCardsQueryVariables>(Queries.card.getCards, {
    variables: {
      input: {
        ids: neededCardIds,
      },
    },
    skip: isEmpty(neededCardIds),
    onCompleted: data => {
      data.getCards?.forEach(card => {
        localCards.current.set(card.id, {
          id: card.id,
          name: card.name,
          isSelected: false,
        });
      });
    },
  });

  const cards = useMemo(() => {
    const selected: Card[] = [];
    const cardsToPresent: Card[] = [];
    const neededCards: string[] = [];

    selectedCardIds?.forEach(value => {
      const card = localCards.current.get(value);
      if (isNil(card)) {
        neededCards.push(value);
      } else {
        selected.push({
          ...card,
          isSelected: true,
        });
      }
    });

    listSummaryCards.data?.listSummaryCards?.cards?.forEach(entity => {
      cardsToPresent.push({
        id: entity.id,
        name: entity.name,
        isSelected: !!selectedCardIds?.includes(entity.id),
      });
    });
    setNeededCards(neededCards);
    setSelectedCards(selected);
    return cardsToPresent;
  }, [selectedCardIds, listSummaryCards.data?.listSummaryCards.cards]);

  const reset = useCallback(() => _setKeyword(''), []);

  return {
    keyword,
    reset,
    onSearch: setKeyword,
    selectedCards,
    cards,
    cardsHasMore: keyword.length === 0 && listSummaryCards.data?.listSummaryCards?.hasMore,
    loading: listSummaryCards.loading || getCards.loading,
    onToggleDefault: onCardSelected,
  };
}

export function useComponentDefaultRelatedCardSelectionHooks(
  component: RelatedCardComponent,
): DefaultRelatedCardSelectionHooks {
  const dispatch = useWorkflowDispatch();

  function newCardIdDefaults(cardId: string): string[] {
    if (component.default?.cardIds?.includes(cardId)) {
      return component.default.cardIds.filter(item => item !== cardId);
    }
    return (component.default?.cardIds ?? []).concat([cardId]);
  }

  function onPress(cardId: string) {
    const newDefaults = newCardIdDefaults(cardId);
    dispatch({
      type: 'updateComponent',
      args: {
        ...component,
        default: { token: undefined, cardIds: newDefaults },
      } as RelatedCardComponent,
    });
  }

  return useDefaultRelatedCardHooks(
    component.workflowTemplateId ?? null,
    component?.default?.cardIds,
    onPress,
  );
}
